import * as THREE from 'three';

export function createNetworkModel(canvasElement, nodeColor, highlightColor) {
  const scene = new THREE.Scene();

  const renderer = new THREE.WebGLRenderer({
    canvas: canvasElement,
    alpha: true,
    context: canvasElement.getContext("webgl", { preserveDrawingBuffer: true }),
  });

  renderer.setPixelRatio(window.devicePixelRatio);

  const camera = new THREE.PerspectiveCamera(75, 1, 0.1, 1000);
  camera.position.set(35, 0, 0);
  camera.lookAt(0, 0, 0);

  function resizeRenderer() {
    const bounds = canvasElement.getBoundingClientRect();
    renderer.setSize(bounds.width, bounds.height, false);
    camera.aspect = bounds.width / bounds.height;
    camera.updateProjectionMatrix();
  }

  resizeRenderer();
  setTimeout(resizeRenderer, 100);
  window.addEventListener('resize', resizeRenderer);

  // Lighting
  scene.add(new THREE.AmbientLight(0xffffff, 0.1));

  const whiteLight = new THREE.PointLight(0xffffff, 60000);
  whiteLight.position.set(20, 60, 23);
  scene.add(whiteLight);

  const accentLight = new THREE.PointLight(highlightColor, 60000);
  accentLight.position.set(-15, 38, -50);
  scene.add(accentLight);

  // Materials
  const nodeMaterial = new THREE.MeshPhongMaterial({ color: nodeColor });

  const networkGroup = new THREE.Object3D();
  scene.add(networkGroup);

  function createNode(position, radius = 0.8) {
    const geometry = new THREE.SphereGeometry(radius, 32, 32);
    const node = new THREE.Mesh(geometry, nodeMaterial.clone());
    node.position.copy(position);
    return node;
  }

  function createConnection(p1, p2, radius = 0.2) {
    const direction = new THREE.Vector3().subVectors(p2, p1);
    const length = direction.length();
    const midpoint = new THREE.Vector3().addVectors(p1, p2).multiplyScalar(0.5);

    const geometry = new THREE.CylinderGeometry(radius, radius, length, 16);
    const material = new THREE.MeshPhongMaterial({ color: nodeColor });
    const connection = new THREE.Mesh(geometry, material);

    connection.position.copy(midpoint);
    connection.quaternion.setFromUnitVectors(
      new THREE.Vector3(0, 1, 0),
      direction.clone().normalize()
    );

    return connection;
  }

  // Network structure
  const layerSizes = [1, 4, 4, 4, 4, 3];
  const layerSpacing = 8;
  const nodeSpacing = 5;
  const layers = [];

  for (let layerIndex = 0; layerIndex < layerSizes.length; layerIndex++) {
    const layer = [];
    const numNodes = layerSizes[layerIndex];

    for (let i = 0; i < numNodes; i++) {
      const z = (i - (numNodes - 1) / 2) * nodeSpacing;
      const y = layerIndex * layerSpacing - 20;
      const position = new THREE.Vector3(0, y, z);
      const node = createNode(position);
      networkGroup.add(node);
      layer.push({ position, mesh: node });
    }

    layers.push(layer);
  }

  const connections = [];

  for (let i = 0; i < layers.length - 1; i++) {
    const currentLayer = layers[i];
    const nextLayer = layers[i + 1];

    for (const fromNode of currentLayer) {
      for (const toNode of nextLayer) {
        const baseRadius = Math.random() * 0.13 + 0.07;
        const edge = createConnection(fromNode.position, toNode.position, baseRadius);
        networkGroup.add(edge);

        connections.push({
          mesh: edge,
          baseRadius,
          timeOffset: Math.random() * Math.PI * 2,
        });
      }
    }
  }

  let animationId = null;
  let isActive = false;
  let startTime = Date.now();

  function animate() {
    if (!isActive) return;

    animationId = requestAnimationFrame(animate);
    const time = Date.now() - startTime;
    const t = time * 0.003;

    for (const conn of connections) {
      const scaleFactor = 1 + 0.5 * Math.sin(t + conn.timeOffset);
      conn.mesh.scale.set(scaleFactor, 1, scaleFactor);
    }

    networkGroup.rotation.y = Math.sin(t * 0.5) * 0.05;
    networkGroup.position.x = Math.sin(t * 0.08) * 1.5;

    renderer.render(scene, camera);
  }

  canvasElement.__threeResize = resizeRenderer;

  // Return controller object
  return {
    start: () => {
      if (!isActive) {
        isActive = true;
        animate();
      }
    },
    pause: () => {
      isActive = false;
      if (animationId) {
        cancelAnimationFrame(animationId);
        animationId = null;
      }
    },
    render: () => {
      if (isActive) {
        renderer.render(scene, camera);
      }
    }
  };
}
